import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  HomeIcon, 
  ChartBarIcon, 
  MagnifyingGlassIcon, 
  BoltIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { 
  HomeIcon as HomeIconSolid, 
  ChartBarIcon as ChartBarIconSolid, 
  MagnifyingGlassIcon as MagnifyingGlassIconSolid, 
  BoltIcon as BoltIconSolid,
  ArrowRightOnRectangleIcon as ArrowRightOnRectangleIconSolid
} from '@heroicons/react/24/solid';

interface NavItem {
  name: string;
  href?: string;
  onClick?: () => void;
  icon: React.ComponentType<{ className?: string }>;
  iconSolid: React.ComponentType<{ className?: string }>;
  badge?: number;
}

const MobileBottomNavigation: React.FC = () => {
  const location = useLocation();
  const { logout } = useAuth();

  const navItems: NavItem[] = [
    {
      name: 'Inventory',
      href: '/inventory',
      icon: HomeIcon,
      iconSolid: HomeIconSolid,
    },
    {
      name: 'PokeData',
      href: '/pokedata',
      icon: ChartBarIcon,
      iconSolid: ChartBarIconSolid,
    },
    {
      name: 'Search',
      href: '/tcg-box-search',
      icon: MagnifyingGlassIcon,
      iconSolid: MagnifyingGlassIconSolid,
    },
    {
      name: 'TCG Oracle',
      href: '/tcg-oracle',
      icon: BoltIcon,
      iconSolid: BoltIconSolid,
    },
    {
      name: 'Logout',
      onClick: logout,
      icon: ArrowRightOnRectangleIcon,
      iconSolid: ArrowRightOnRectangleIconSolid,
    },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden" style={{ backgroundColor: 'var(--color-secondary-dark)', borderTopColor: 'var(--color-accent-dark)' }}>
      <div className="grid grid-cols-5 h-16 border-t" style={{ borderColor: 'var(--color-accent-dark)' }}>
        {navItems.map((item) => {
          const isActive = item.href ? location.pathname === item.href : false;
          const IconComponent = isActive ? item.iconSolid : item.icon;
          
          const baseClassName = `
            flex flex-col items-center justify-center px-1 py-2 text-xs font-medium
            transition-all duration-200 relative
            ${isActive 
              ? 'text-white' 
              : 'text-gray-400 hover:text-gray-200 active:bg-slate-700'
            }
          `;

          if (item.href) {
            // Navigation item with Link
            return (
              <Link
                key={item.name}
                to={item.href}
                className={baseClassName}
                style={isActive ? { backgroundColor: 'var(--color-interactive)' } : {}}
              >
                <div className="relative">
                  <IconComponent className="h-6 w-6 mb-1" />
                  {item.badge && (
                    <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </div>
                <span className="truncate max-w-full">{item.name}</span>
                {isActive && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-white rounded-full"></div>
                )}
              </Link>
            );
          } else {
            // Action item with button (like logout)
            return (
              <button
                key={item.name}
                onClick={item.onClick}
                className={baseClassName}
              >
                <div className="relative">
                  <IconComponent className="h-6 w-6 mb-1" />
                  {item.badge && (
                    <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </div>
                <span className="truncate max-w-full">{item.name}</span>
              </button>
            );
          }
        })}
      </div>
    </div>
  );
};

export default MobileBottomNavigation;
